import React, { MutableRefObject, useEffect, useMemo, useRef } from 'react';

import { FlexGrid } from '../FlexGrid';
import { FlexGridItem } from '../FlexGridItem';
import { Submenu } from '../Submenu';
import { SubmenuLinkProps } from '../Submenu/Submenu.types';
import { ScrollSpyProps } from './ScrollSpy.types';
import { ScrollSpyContent } from './ScrollSpyContent';
import useScrollSpy from './useScrollSpy';

const pageWrapId = 'page-wrap';

export const ScrollSpy = ({
  sections,
  testid,
}: ScrollSpyProps): JSX.Element => {
  const pageWrapEl: MutableRefObject<Element> = useRef();
  const { activeIndex, updateEntries } = useScrollSpy();

  const links: SubmenuLinkProps[] = useMemo(
    () =>
      sections.map(({ id, displayText }, index) => ({
        href: `#${id}`,
        displayText,
        active: index === activeIndex,
      })),
    [activeIndex, sections]
  );

  /**
   * Sticky navigation does not work with the styles applied to the pageWrapId
   */
  useEffect(() => {
    pageWrapEl.current = document.getElementById(pageWrapId);

    if (pageWrapEl.current) {
      pageWrapEl.current.removeAttribute('id');
    }

    return () => {
      if (pageWrapEl.current) {
        pageWrapEl.current.setAttribute('id', pageWrapId);
      }
    };
  }, []);

  /**
   * Update the URL hash on scroll
   */
  useEffect(() => {
    if (activeIndex !== null) {
      const sectionId = `#${sections[activeIndex].id}`;

      history.replaceState({ sectionId }, '', sectionId);
    } else if (history.state?.sectionId !== null) {
      history.replaceState({ sectionId: null }, '', ' ');
    }
  }, [activeIndex, sections]);

  return (
    <FlexGrid hasGutters shouldWrap testid={testid}>
      <FlexGridItem xs={12} lgP={3} className='sticky-sidenav'>
        <Submenu altLayout={true} links={links} />
      </FlexGridItem>
      <FlexGridItem xs={0} lgP={1} />
      <FlexGridItem xs={12} lgP={8}>
        {sections.map(({ id, children }, index) => (
          <ScrollSpyContent
            key={index}
            index={index}
            sectionId={id}
            updateEntries={updateEntries}
          >
            {children}
          </ScrollSpyContent>
        ))}
      </FlexGridItem>
    </FlexGrid>
  );
};
